import sanitizeHtml, { type IOptions } from "sanitize-html";

/**
 * Rich product / supplier descriptions (HTML from external sources).
 * Scripts, event handlers, and dangerous URLs are stripped.
 */
const PRODUCT_OPTIONS: IOptions = {
  allowedTags: [
    "a",
    "abbr",
    "b",
    "blockquote",
    "br",
    "caption",
    "code",
    "div",
    "em",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
    "i",
    "img",
    "li",
    "ol",
    "p",
    "pre",
    "small",
    "span",
    "strike",
    "strong",
    "sub",
    "sup",
    "table",
    "tbody",
    "td",
    "tfoot",
    "th",
    "thead",
    "tr",
    "u",
    "ul",
  ],
  allowedAttributes: {
    "*": ["class", "style"],
    a: ["href", "title", "target", "rel"],
    img: ["src", "alt", "width", "height", "referrerpolicy"],
    th: ["colspan", "rowspan", "width"],
    td: ["colspan", "rowspan", "width"],
  },
  allowedStyles: {
    "*": {
      color: [/.*/],
      "background-color": [/.*/],
      "font-size": [/.*/],
      "font-weight": [/.*/],
      "font-style": [/.*/],
      "font-family": [/.*/],
      "text-align": [/.*/],
      "text-decoration": [/.*/],
      "line-height": [/.*/],
      "letter-spacing": [/.*/],
      width: [/.*/],
      "max-width": [/.*/],
      height: [/.*/],
      "min-height": [/.*/],
      margin: [/.*/],
      "margin-top": [/.*/],
      "margin-bottom": [/.*/],
      "margin-left": [/.*/],
      "margin-right": [/.*/],
      padding: [/.*/],
      "padding-top": [/.*/],
      "padding-bottom": [/.*/],
      "padding-left": [/.*/],
      "padding-right": [/.*/],
      border: [/.*/],
      "border-collapse": [/.*/],
      display: [/.*/],
      "vertical-align": [/.*/],
      "list-style-type": [/.*/],
      "white-space": [/.*/],
      "word-break": [/.*/],
      overflow: [/.*/],
    },
  },
  allowedSchemes: ["http", "https", "mailto"],
  allowProtocolRelative: false,
  transformTags: {
    a: sanitizeHtml.simpleTransform("a", { rel: "noopener noreferrer" }),
    // Force referrerpolicy on every image, overriding whatever the supplier
    // sent. Done as a parser hook rather than a regex over the raw markup so
    // it cannot be defeated by unusual but valid tag syntax.
    img: sanitizeHtml.simpleTransform(
      "img",
      { referrerpolicy: "no-referrer" },
      true
    ),
  },
};

/**
 * Short snippets: plan feature bullets, banner copy, modal one-liners.
 *
 * These strings come from the billing/plans API and only ever need inline
 * emphasis, so the allowlist is deliberately much tighter than the product
 * profile: no links, no media, no block layout, no styles, no URL schemes.
 * `s`/`strike` are allowed because crossed-off plan features are expressed as
 * `<s>...</s>` (see `isFeatureCrossedOff`).
 */
const INLINE_OPTIONS: IOptions = {
  allowedTags: [
    "b",
    "br",
    "em",
    "i",
    "mark",
    "s",
    "small",
    "span",
    "strike",
    "strong",
    "sub",
    "sup",
    "u",
  ],
  allowedAttributes: {
    "*": ["class"],
  },
  allowedSchemes: [],
};

export function sanitizeProductDescriptionHtml(
  html: string | null | undefined
): string {
  if (html == null || html === "") return "";
  return sanitizeHtml(String(html), PRODUCT_OPTIONS);
}

export function sanitizeInlineHtml(html: string | null | undefined): string {
  if (html == null || html === "") return "";
  return sanitizeHtml(String(html), INLINE_OPTIONS);
}
